import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner, ProgressBar } from 'react-bootstrap';
import authService from '../services/authService';
import businessService from '../services/businessService';
import BackToDashboard from '../components/BackToDashboard';

function CustomerExperience() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('loading'); // loading, interview, extracting, review, complete
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Interview state
  const [conversation, setConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [currentStage, setCurrentStage] = useState('purchase');
  const [stageNumber, setStageNumber] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [isResponding, setIsResponding] = useState(false);

  // Journey map data
  const [journeyMap, setJourneyMap] = useState({
    purchase: '',
    onboarding: '',
    ongoing_use: '',
    success_metrics: '',
    retention_touchpoints: ''
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  // Focus input after AI responds
  useEffect(() => {
    if (!isResponding && stage === 'interview' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isResponding, stage]);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if customer experience already exists
    const experienceResult = await businessService.getField('customer_experience');

    if (experienceResult.success && experienceResult.data.field_value) {
      // Load existing journey map
      try {
        const existingJourney = JSON.parse(experienceResult.data.field_value);
        setJourneyMap(existingJourney);
        setStage('review');
        setLoading(false);
      } catch (error) {
        console.error('Error parsing existing journey:', error);
        // Start fresh interview
        startInterview();
      }
    } else {
      // Start the interview
      startInterview();
    }
  };

  const startInterview = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/customer-experience/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setConversation(data.conversation);
        setCurrentStage(data.stage);
        setStageNumber(data.stage_number);
        setStage('interview');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to start interview');
      }
    } catch (err) {
      setError('Error starting interview: ' + err.message);
    }

    setLoading(false);
  };

  const sendResponse = async () => {
    if (!userInput.trim() || isResponding) return;

    const message = userInput.trim();
    setUserInput('');
    setError('');
    setIsResponding(true);

    // Add user message to conversation immediately
    const updatedConversation = [...conversation, { role: 'user', content: message }];
    setConversation(updatedConversation);

    try {
      const response = await fetch('/api/customer-experience/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: message,
          conversation: conversation, // Send original, backend will add user message
          stage: currentStage
        })
      });

      if (response.ok) {
        const data = await response.json();
        setConversation(data.conversation);
        setCurrentStage(data.stage);
        setStageNumber(data.stage_number);
        setIsComplete(data.is_complete);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to get response');
      }
    } catch (err) {
      setError('Error sending response: ' + err.message);
    }

    setIsResponding(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendResponse();
    }
  };

  const extractJourneyMap = async () => {
    setStage('extracting');
    setError('');

    try {
      const response = await fetch('/api/customer-experience/extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ conversation })
      });

      if (response.ok) {
        const data = await response.json();
        setJourneyMap(data.journey_map || {});
        setStage('review');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to extract journey map');
        setStage('interview');
      }
    } catch (err) {
      setError('Error extracting journey map: ' + err.message);
      setStage('interview');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    const result = await businessService.saveField('customer_experience', JSON.stringify(journeyMap));

    setSaving(false);

    if (result.success) {
      setStage('complete');
      setTimeout(() => {
        navigate('/competitive-analysis');
      }, 1500);
    } else {
      setError(result.error || 'Failed to save journey map');
    }
  };

  const handleStartOver = () => {
    setConversation([]);
    setJourneyMap({
      purchase: '',
      onboarding: '',
      ongoing_use: '',
      success_metrics: '',
      retention_touchpoints: ''
    });
    setIsComplete(false);
    setCurrentStage('purchase');
    setStageNumber(1);
    startInterview();
  };

  const handleSaveConversation = () => {
    // Format conversation as readable text
    const timestamp = new Date().toLocaleString();
    let text = `Customer Experience Interview\n`;
    text += `${'='.repeat(30)}\n`;
    text += `Saved: ${timestamp}\n\n`;

    conversation.forEach((msg) => {
      const speaker = msg.role === 'assistant' ? 'AI Interviewer' : 'You';
      text += `--- ${speaker} ---\n`;
      text += `${msg.content}\n\n`;
    });

    text += `\n--- End of Conversation ---\n`;
    text += `\nThis conversation contains your detailed thoughts about your customer experience journey.\n`;
    text += `Use it for sales pages, onboarding docs, support materials, and training.\n`;

    // Create and download file
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-experience-interview-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const stageLabels = {
    'purchase': 'Purchase',
    'onboarding': 'Onboarding',
    'ongoing_use': 'Ongoing Use',
    'success_metrics': 'Success Metrics',
    'retention': 'Retention',
    'complete': 'Complete'
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  // Interview stage
  if (stage === 'interview') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h4 className="mb-1">Customer Experience Interview</h4>
                    <p className="text-muted small mb-0">Tell me about your customer's journey</p>
                  </div>
                  <span className="badge bg-primary">
                    Stage {stageNumber}/5: {stageLabels[currentStage]}
                  </span>
                </div>

                {/* Progress bar */}
                <ProgressBar
                  now={(stageNumber / 5) * 100}
                  className="mb-3"
                  variant="primary"
                  style={{ height: '8px' }}
                />

                {/* Messages Container */}
                <div
                  className="border rounded p-3 mb-3"
                  style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  {conversation.map((msg, index) => (
                    <div
                      key={index}
                      className={`mb-3 ${msg.role === 'user' ? 'text-end' : ''}`}
                    >
                      <div
                        className={`d-inline-block p-3 rounded ${
                          msg.role === 'user'
                            ? 'bg-primary text-white'
                            : 'bg-white border'
                        }`}
                        style={{
                          maxWidth: '85%',
                          textAlign: 'left'
                        }}
                      >
                        {msg.role === 'assistant' && (
                          <small className="text-muted d-block mb-1">
                            <strong>🤖 AI Interviewer</strong>
                          </small>
                        )}
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {msg.content.split('\n').map((line, i) => {
                            // Handle bold text
                            const parts = line.split(/(\*\*.*?\*\*)/g);
                            const formatted = parts.map((part, j) => {
                              if (part.startsWith('**') && part.endsWith('**')) {
                                return <strong key={j}>{part.slice(2, -2)}</strong>;
                              }
                              return part;
                            });
                            return (
                              <span key={i}>
                                {formatted}
                                {i < msg.content.split('\n').length - 1 && <br />}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}

                  {isResponding && (
                    <div className="mb-3">
                      <div className="d-inline-block p-3 rounded bg-white border">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span className="text-muted">Thinking...</span>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

                {/* Interview complete message */}
                {isComplete && (
                  <Alert variant="success" className="mb-3">
                    <strong>Great conversation!</strong> I have all the information I need.
                    Click "Generate Journey Map" to create your polished customer experience document.
                  </Alert>
                )}

                {/* Input Area */}
                {!isComplete ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your answer..."
                        disabled={isResponding}
                        ref={inputRef}
                      />
                      <Form.Text className="text-muted">
                        Press Enter to send, Shift+Enter for new line
                      </Form.Text>
                    </Form.Group>

                    <div className="d-flex justify-content-between">
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/dashboard/business-model')}
                        disabled={isResponding}
                      >
                        ← Save for Later
                      </Button>

                      <div>
                        {conversation.length >= 2 && (
                          <Button
                            variant="outline-info"
                            onClick={handleSaveConversation}
                            className="me-2"
                            title="Download conversation as text file"
                          >
                            Save Conversation
                          </Button>
                        )}
                        <Button
                          variant="primary"
                          onClick={sendResponse}
                          disabled={!userInput.trim() || isResponding}
                        >
                          {isResponding ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Sending...
                            </>
                          ) : (
                            'Send'
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="d-flex justify-content-between">
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/dashboard/business-model')}
                    >
                      ← Save for Later
                    </Button>

                    <div>
                      <Button
                        variant="outline-info"
                        onClick={handleSaveConversation}
                        className="me-2"
                        title="Download conversation as text file"
                      >
                        Save Conversation
                      </Button>
                      <Button
                        variant="success"
                        size="lg"
                        onClick={extractJourneyMap}
                      >
                        Generate Journey Map →
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Extracting stage
  if (stage === 'extracting') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5 text-center">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h4>Creating your Customer Experience Journey Map...</h4>
                <p className="text-muted">
                  Summarizing everything you shared into a polished document.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Review stage
  if (stage === 'review') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={9}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">Your Customer Experience Journey</h2>

                <p className="text-muted mb-4">
                  Review and edit each phase. This is based on what you shared in the interview.
                </p>

                {error && <Alert variant="danger">{error}</Alert>}

                <Form>
                  <Card className="mb-3 border-primary">
                    <Card.Header className="bg-primary text-white">
                      <strong>PURCHASE</strong>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group>
                        <Form.Label>How do they buy? What happens immediately after purchase?</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={journeyMap.purchase}
                          onChange={(e) => setJourneyMap({...journeyMap, purchase: e.target.value})}
                          placeholder="Describe the purchase experience..."
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3 border-info">
                    <Card.Header className="bg-info text-white">
                      <strong>ONBOARDING</strong>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group>
                        <Form.Label>First days experience, time to first win, support during setup</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          value={journeyMap.onboarding}
                          onChange={(e) => setJourneyMap({...journeyMap, onboarding: e.target.value})}
                          placeholder="Describe the onboarding experience..."
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3 border-success">
                    <Card.Header className="bg-success text-white">
                      <strong>ONGOING USE</strong>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group>
                        <Form.Label>Regular interaction patterns, what brings them back, support needs</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          value={journeyMap.ongoing_use}
                          onChange={(e) => setJourneyMap({...journeyMap, ongoing_use: e.target.value})}
                          placeholder="Describe ongoing use patterns..."
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3 border-warning">
                    <Card.Header className="bg-warning">
                      <strong>SUCCESS METRICS</strong>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group>
                        <Form.Label>How do they know it's working? What defines success?</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={journeyMap.success_metrics}
                          onChange={(e) => setJourneyMap({...journeyMap, success_metrics: e.target.value})}
                          placeholder="Describe success metrics..."
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  <Card className="mb-4 border-secondary">
                    <Card.Header className="bg-secondary text-white">
                      <strong>RETENTION TOUCHPOINTS</strong>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group>
                        <Form.Label>Check-ins, value reminders, re-engagement strategies</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={journeyMap.retention_touchpoints}
                          onChange={(e) => setJourneyMap({...journeyMap, retention_touchpoints: e.target.value})}
                          placeholder="Describe retention touchpoints..."
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  <div className="d-flex justify-content-between">
                    <Button
                      variant="outline-secondary"
                      onClick={handleStartOver}
                    >
                      Start Over
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save & Continue'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Complete stage
  if (stage === 'complete') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow border-success">
              <Card.Body className="p-4 text-center">
                <h2 className="mb-4">Customer Experience Saved!</h2>
                <Alert variant="success">
                  Your customer journey map has been saved successfully.
                </Alert>
                <p className="text-muted">
                  Redirecting to Competitive Analysis...
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default CustomerExperience;
